<template>
  <div class="flex flex-col gap-4 text-left" data-testid="invite-preview-notices">
    <div
      class="bg-primary5 border border-primary/20 rounded-md px-4 py-3 text-sm text-textPrimary"
      data-testid="invite-what-happens"
    >
      <p class="font-semibold mb-1">O que acontece quando você confirmar</p>
      <ul v-if="fillMode === 'INVITE'" class="list-disc pl-5 m-0">
        <li>Ninguém é alocado agora.</li>
        <li>As vagas continuam abertas em relatórios, contagens e exportações.</li>
        <template v-if="doctorCount === 1">
          <li>O médico recebe o convite no aplicativo e por e-mail.</li>
          <li v-if="eligibleCount > 0">Ele fica com {{ eligibleStationsText }} quando aceitar.</li>
        </template>
        <template v-else>
          <li>Os médicos recebem o convite no aplicativo e por e-mail.</li>
          <li v-if="eligibleCount > 0">
            Quem aceitar primeiro fica com {{ eligibleStationsText }}. Os outros recebem um aviso.
          </li>
        </template>
        <li v-if="replacementNotice">{{ replacementNotice }}</li>
        <li v-if="deadline" data-testid="invite-deadline">
          O convite vence em <strong>{{ deadline.when }}</strong
          >, {{ deadline.reason }}.
        </li>
      </ul>
      <ul v-else class="list-disc pl-5 m-0">
        <li>O médico ocupa os plantões agora, sem aceite do Termo de Ciência.</li>
        <li>O médico recebe um aviso no aplicativo.</li>
      </ul>
    </div>

    <p
      v-if="startedNotice"
      class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2"
      data-testid="invite-started-notice"
    >
      {{ startedNotice }}
    </p>

    <p
      v-for="(notice, index) in issueNotices"
      :key="index"
      class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2"
      data-testid="invite-issue-notice"
    >
      <span class="font-semibold">Aviso:</span> {{ notice }}
    </p>

    <p
      v-if="pendingNotice"
      class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2"
      data-testid="pending-commitments-notice"
    >
      {{ pendingNotice }}
    </p>

    <p
      v-if="pendingFailedNotice"
      class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-2"
      data-testid="pending-commitments-failed"
    >
      {{ pendingFailedNotice }}
    </p>

    <p
      v-if="fillMode === 'INVITE' && inviteeNames.length"
      class="text-sm text-textPrimary"
      data-testid="invite-also-invited"
    >
      <span class="font-semibold">Também convidados:</span> {{ inviteeNames.join(', ') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type {
  AllocationInvitePreview,
  PendingCommitment
} from '@/Network/Types/Responses/AllocationInviteResponses'
import {
  buildDoctorIssueNotices,
  buildStartedNotice,
  hasPendingCommitment,
  PENDING_CHECK_FAILED_NOTICE,
  pluralizeStations,
  splitInviteDeadline,
  type FillMode,
  type ReplacementChoice
} from './inviteWizard'

// Passo 3 do assistente de convite (acceptance-terms WEB-1): só apresentação; o modal carrega
// a prévia (convite) ou as pendências (alocação direta) e decide o envio.
const props = defineProps<{
  fillMode: FillMode
  /** Prévia do convite (só no fluxo de convite). */
  preview: AllocationInvitePreview | null
  inviteeNames: string[]
  replacement: ReplacementChoice
  hasCurrentDoctor: boolean
  /** Pendências (só na alocação direta). */
  pendingCommitments: PendingCommitment[]
  /** Falha ao consultar as pendências (só na alocação direta): o aviso vira genérico. */
  pendingCheckFailed: boolean
  doctorName: (doctorId: number) => string
  timezone: string
  /** Principal + convidados: muda o texto do singular para o plural. */
  doctorCount: number
}>()

const eligibleCount = computed(() => props.preview?.eligibleStationIds.length ?? 0)
/** "o plantão" / "os 4 plantões". */
const eligibleStationsText = computed(() =>
  eligibleCount.value === 1 ? 'o plantão' : `os ${pluralizeStations(eligibleCount.value)}`
)
const deadline = computed(() =>
  props.preview ? splitInviteDeadline(props.preview, props.timezone) : null
)
// Os avisos por médico são do convite; na direta a prévia não existe (ou ficou de antes).
const issueNotices = computed(() =>
  props.fillMode === 'INVITE' && props.preview
    ? buildDoctorIssueNotices(props.preview, props.doctorName)
    : []
)

const startedNotice = computed(() =>
  props.fillMode === 'INVITE' && props.preview ? buildStartedNotice(props.preview) : null
)

const pendingNotice = computed(() => {
  if (props.fillMode !== 'DIRECT') return null
  const n = props.pendingCommitments.filter(hasPendingCommitment).length
  if (n === 0) return null
  return `${pluralizeStations(n)} ${n === 1 ? 'tem' : 'têm'} troca ou convite pendente. Ao confirmar, o pendente é cancelado e os envolvidos são avisados.`
})

const pendingFailedNotice = computed(() =>
  props.fillMode === 'DIRECT' && props.pendingCheckFailed ? PENDING_CHECK_FAILED_NOTICE : null
)

const replacementNotice = computed(() => {
  if (props.fillMode !== 'INVITE' || !props.hasCurrentDoctor) return null
  return props.replacement === 'NOW'
    ? 'O médico atual sai agora e recebe um aviso.'
    : 'O médico atual continua no plantão até o aceite e recebe um aviso se o convite cair.'
})
</script>
